import { FileText } from 'lucide-react';
import { MAX_DEBUG_HISTORY_CALLS } from 'Modules/softphone/components/SoftphoneDevTools/constants';
import { DevToolsButton } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsButton';
import { DevToolsEmptyState } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsEmptyState';
import { DevToolsSectionHeader } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsSectionHeader';
import { DebugAboutPanel } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/DebugTab/DebugAboutPanel';
import type { DebugHistoryListProps } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/DebugTab/DebugHistoryList/index.types';
import { DebugInteractionGroup } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/DebugTab/DebugInteractionGroup';
import React from 'react';

/**
 * DebugHistoryList - The captured interactions, newest capture first
 *
 * This is the master half of the tab.
 */
export const DebugHistoryList: React.FC<DebugHistoryListProps> = ({
  interactions,
  recordCount,
  onOpenLeg,
  onDownloadInteraction,
  onDownloadAll,
  onClear,
}) => (
  <div className="flex flex-col gap-2.5">
    <div className="flex items-center gap-2">
      {/* The count is records, not interactions: it is what the 50-record cap
          applies to, so it has to be the number the panel shows. */}
      <DevToolsSectionHeader title="Debug history" count={recordCount} />
      <div className="flex-1" />
      {recordCount > 0 && (
        <>
          {/* With a single interaction its own export is the same file, so a
              second button beside it would only be a second way to say it. */}
          {interactions.length > 1 && (
            <DevToolsButton onClick={onDownloadAll}>Download all</DevToolsButton>
          )}
          <DevToolsButton variant="destructive" onClick={onClear}>
            Clear
          </DevToolsButton>
        </>
      )}
    </div>

    {interactions.length > 0 ? (
      interactions.map((interaction) => (
        <DebugInteractionGroup
          key={interaction.id}
          interaction={interaction}
          onOpenLeg={onOpenLeg}
          onDownload={() => onDownloadInteraction(interaction)}
        />
      ))
    ) : (
      <DevToolsEmptyState
        icon={<FileText className="h-[21px] w-[21px]" strokeWidth={1.7} />}
        title="No debug records yet"
        body="A record is captured automatically when a call ends, one per leg — and the legs of a transfer or a conference are grouped as one interaction."
        hint={`keeps the last ${MAX_DEBUG_HISTORY_CALLS} records, not the last ${MAX_DEBUG_HISTORY_CALLS} calls`}
      />
    )}

    {recordCount > 0 && <DebugAboutPanel />}
  </div>
);
